'use client';

import { IPrice, PriceModel } from '@/services/models/Price';
import { Nullable } from '@/type-utils';
import { FunctionComponent, useMemo } from 'react';

import { MoneyModel, stripDecimalsIfWhole } from '@/services/models/Money';
import { observer } from 'mobx-react-lite';
import { classes } from '@/next-utils/css-utils/scss-utils';
import { msgf } from '@/services/isomorphic/I18NService';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { isNullish } from '@/utils/null-utils';
import IStylable from '../../traits/IStylable';
import SkeletonLoader from '../../utility/SkeletonLoader';
import S from './styles.base.module.scss';
import { product_price_originalPrice } from "@/lang/__generated__/ahnu/product_price_originalPrice";
import { product_price_discountPercent } from "@/lang/__generated__/ahnu/product_price_discountPercent";

/**
 * Determine if percentage off is on same line as price or under
 */
export type PriceVariant = 'under' | 'adjacent';

export interface IPriceProps extends IStylable {
  /** The price to be converted. */
  price: Nullable<IPrice>;
  /** Whether to show discount text when on sale. Defaults to true. */
  showDiscountText?: boolean;
  /**
   * Position of strikethrough price relative to sale price. Defaults to
   * 'right'.
   */
  strikethroughPosition?: 'left' | 'right';
  /** Context variant; adds a top-level class to the container (e.g. for PDP vs PLP styling). */
  variant?: PriceVariant;
}

/** Size selector component for the pdp. */
export const Price: FunctionComponent<IPriceProps> = observer(function Price({
  price,
  className,
  style,
  showDiscountText = true,
  strikethroughPosition = 'right',
  variant = 'under'
}) {
  // Derive model and discount content unconditionally so hooks run in a fixed order.
  const priceModel = price ? PriceModel.from(price) : null;
  const isOnSale = priceModel?.isOnSale ?? false;
  const discountTextComponent = useMemo(
    () =>
      priceModel &&
      !isNullish(priceModel.discountPercent) &&
      showDiscountText && (
        <DiscountText
          percent={priceModel.discountPercent}
          originalPrice={priceModel.originalPrice!}
          variant={variant}
        />
      ),
    [priceModel, showDiscountText, variant]
  );

  // If the product model hasn't loaded, show a skeleton loader (after all hooks).
  if (!price) return <SkeletonLoader className={S.skeletonLoader} />;

  const model = priceModel!;
  return (
    <div
      className={classes(S.container, variant && S[variant], className)}
      style={style}
    >
      {isOnSale ? (
        <>
          <div className={classes(S.pricesRow)}>
            {strikethroughPosition === 'left' ? (
              <>
                <OriginalCost amount={model.originalPrice!} />
                <CurrentCost amount={model.price} isSale />
              </>
            ) : (
              <>
                <CurrentCost amount={model.price} isSale />
                <OriginalCost amount={model.originalPrice!} />
              </>
            )}

            {variant === 'adjacent' && discountTextComponent}
          </div>
          {variant === 'under' && discountTextComponent}
        </>
      ) : (
        <CurrentCost amount={model.price} isSale={false} />
      )}
    </div>
  );
});

/**
 * Props for rendering money.
 */
interface IMoneyProps {
  /**
   * The amount of money to display.
   */
  amount: MoneyModel;

  /**
   * Whether this is a sale price (should be bold).
   */
  isSale?: boolean;
}

/**
 * Props for rendering discount text.
 */
interface IDiscountTextProps {
  /**
   * The discount percentage to display.
   */
  percent: number;

  /**
   * The original price for EMEA "Original Price XX" format.
   */
  originalPrice: MoneyModel;

  /**
   * Context variant (PDP vs PLP). Parentheses around percent-off are shown only for PDP.
   */
  variant?: PriceVariant;
}

/**
 * Renders the original price with strikethrough styling when on sale.
 * @param props - The props for the component.
 */
const OriginalCost: FunctionComponent<IMoneyProps> = ({ amount }) => {
  return (
    <span className={S.originalPrice}>{stripDecimalsIfWhole(amount)}</span>
  );
};

/**
 * Renders the current/sale price.
 * @param props - The props for the component.
 */
const CurrentCost: FunctionComponent<IMoneyProps> = ({
  amount,
  isSale = false
}) => {
  return (
    <span className={isSale ? S.salePrice : S.regularPrice}>
      {stripDecimalsIfWhole(amount)}
    </span>
  );
};

/**
 * Renders the discount text with region-specific formatting.
 * NA: "(X% off)" - localized
 * EMEA: "Original Price XX" - localized.
 * @param props - The props for the component.
 */
const DiscountText: FunctionComponent<IDiscountTextProps> = ({
  percent,
  originalPrice,
  variant = 'under'
}) => {
  // Get discount text format configuration from product config
  const productConfig = ConfigurationService.getConfig('product');
  const discountFormat = productConfig.getSetting(
    'pricing.discountTextFormat'
  ).value;

  // Determine text and className based on config
  const text =
    // EMEA: 'Original Price XX.XX'
    discountFormat.style.value === 'originalPrice'
      ? msgf(product_price_originalPrice, {
        price: stripDecimalsIfWhole(originalPrice)
      })
      : // NA/default: (X% off) for PDP, X% off (no parens) for PLP
      variant === 'under'
        ? `(${msgf(product_price_discountPercent, { percent })})`
        : msgf(product_price_discountPercent, { percent });

  const className = discountFormat.className.value
    ? classes(S.discount, discountFormat.className.value)
    : S.discount;

  return <span className={className}>{text}</span>;
};
